// src/pages/AdminTags/AdminTags.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Table,
  Badge,
  Button,
  Spinner,
  Alert,
  ButtonGroup,
  Modal,
  Form,
} from "react-bootstrap";
import { Auth } from "aws-amplify";
import AdminNav from "../../components/AdminNav";
import "./AdminTags.css";

const API_BASE = process.env.REACT_APP_API_BASE;

// ✅ Use Access Token (most backend authorizers expect this)
async function authHeader() {
  const session = await Auth.currentSession();
  const token = session.getAccessToken().getJwtToken();
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
}

function statusBadge(status) {
  const s = String(status || "").toLowerCase();
  if (s === "claimed") return <Badge bg="success">Claimed</Badge>;
  if (s === "unclaimed") return <Badge bg="secondary">Unclaimed</Badge>;
  if (s === "blocked") return <Badge bg="danger">Blocked</Badge>;
  return <Badge bg="light">{status || "—"}</Badge>;
}

function fmtDate(msOrIso) {
  if (!msOrIso) return "—";
  const d = typeof msOrIso === "number" ? new Date(msOrIso) : new Date(msOrIso);
  if (Number.isNaN(d.getTime())) return String(msOrIso);
  return d.toLocaleString();
}

export default function AdminTags() {
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);

  const [tags, setTags] = useState([]);
  const [filter, setFilter] = useState("claimed"); // claimed|unclaimed|blocked|all

  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // SMS modal
  const [messageModal, setMessageModal] = useState({
    show: false,
    tag: null,
    text: "",
  });
  const [sending, setSending] = useState(false);

  const filterLabel = useMemo(() => {
    return (
      {
        claimed: "Claimed",
        unclaimed: "Unclaimed",
        blocked: "Blocked",
        all: "All",
      }[filter] || "Claimed"
    );
  }, [filter]);

  const fetchTags = async (status = filter) => {
    setLoading(true);
    setError("");
    setSuccessMsg("");

    try {
      if (!API_BASE) {
        throw new Error(
          "Missing REACT_APP_API_BASE. Set it in your environment and restart the app."
        );
      }

      const headers = await authHeader();

      let url = `${API_BASE}/admin/tags`;
      if (status && status !== "all") {
        url += `?status=${encodeURIComponent(status)}`;
      } else {
        url += `?status=all`;
      }

      const res = await fetch(url, { method: "GET", headers });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed to load tags: ${res.status}`);
      }

      const data = await res.json();
      setTags(data.items || []);
    } catch (err) {
      console.error("Fetch tags error:", err);
      setError(err?.message || "Failed to fetch tags.");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (tagId, status) => {
    setError("");
    setSuccessMsg("");

    try {
      if (!API_BASE) {
        throw new Error(
          "Missing REACT_APP_API_BASE. Set it in your environment and restart the app."
        );
      }

      const headers = await authHeader();

      const res = await fetch(`${API_BASE}/admin/tags/${encodeURIComponent(tagId)}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ status }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed to update status: ${res.status}`);
      }

      // optimistic update
      setTags((prev) => prev.map((t) => (t.tagId === tagId ? { ...t, status } : t)));
      setSuccessMsg(`Updated ${tagId} → ${status}.`);
    } catch (err) {
      console.error("Update tag status error:", err);
      setError(err?.message || "Failed to update tag status.");
    }
  };

  const onFilterChange = async (newFilter) => {
    setFilter(newFilter);
    await fetchTags(newFilter);
  };

  const openMessageModal = (tag) => {
    setError("");
    setSuccessMsg("");
    setMessageModal({
      show: true,
      tag,
      text: `Hi, this is Little Paws Place regarding SmartTag ${tag?.tagId || ""}...`,
    });
  };

  const closeMessageModal = () => {
    setMessageModal({ show: false, tag: null, text: "" });
  };

  const sendMessageToTagOwner = async () => {
    const tagId = messageModal?.tag?.tagId;
    const message = (messageModal?.text || "").trim();

    if (!tagId) {
      setError("No SmartTag selected to message.");
      return;
    }
    if (!message) {
      setError("Message cannot be empty.");
      return;
    }

    setSending(true);
    setError("");
    setSuccessMsg("");

    try {
      if (!API_BASE) {
        throw new Error(
          "Missing REACT_APP_API_BASE. Set it in your environment and restart the app."
        );
      }

      const headers = await authHeader();

      const res = await fetch(
        `${API_BASE}/admin/tags/${encodeURIComponent(tagId)}/message`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({ message }),
        }
      );

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed to send message: ${res.status}`);
      }

      setSuccessMsg(`Message sent to owner of ${tagId}.`);
      closeMessageModal();
    } catch (err) {
      console.error("sendMessageToTagOwner error:", err);
      setError(err?.message || "Failed to send message.");
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const idPayload = user?.signInUserSession?.idToken?.payload || {};
        const groups = idPayload["cognito:groups"] || idPayload["custom:groups"] || [];

        if (Array.isArray(groups) && (groups.includes("Support") || groups.includes("Admin"))) {
          setAuthorized(true);
          await fetchTags("claimed");
        } else {
          setAuthorized(false);
          setLoading(false);
        }
      } catch (err) {
        console.error("Auth error:", err);
        setAuthorized(false);
        setLoading(false);
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading && !tags.length) {
    return (
      <div className="admin-page-wrapper">
        <Container className="admin-page-shell">
          <div className="py-4 text-center">
            <Spinner animation="border" /> Loading SmartTags…
          </div>
        </Container>
      </div>
    );
  }

  if (!authorized) {
    return (
      <div className="admin-page-wrapper">
        <Container className="admin-page-shell">
          <div className="py-4">
            <Alert variant="danger">You’re not authorized to view this page.</Alert>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="admin-page-wrapper">
      <Container className="admin-page-shell">
        <AdminNav />

        <div className="admin-page-header">
          <div className="admin-page-header-left">
            <div className="admin-page-header-icon">🏷️</div>
            <div>
              <h3 className="admin-page-title">SmartTags – {filterLabel}</h3>
              <div className="admin-page-subtitle">
                View and manage claimed, unclaimed, or blocked tags.
              </div>
            </div>
          </div>
        </div>

        <div className="admin-page-body">
          <div className="admin-panel mb-3">
            <div className="admin-filter-row">
              <div />

              <div className="admin-filter-actions">
                <ButtonGroup size="sm">
                  <Button
                    className="admin-filter-btn"
                    variant={filter === "claimed" ? "primary" : "outline-primary"}
                    onClick={() => onFilterChange("claimed")}
                  >
                    Claimed
                  </Button>
                  <Button
                    className="admin-filter-btn"
                    variant={filter === "unclaimed" ? "primary" : "outline-primary"}
                    onClick={() => onFilterChange("unclaimed")}
                  >
                    Unclaimed
                  </Button>
                  <Button
                    className="admin-filter-btn"
                    variant={filter === "blocked" ? "primary" : "outline-primary"}
                    onClick={() => onFilterChange("blocked")}
                  >
                    Blocked
                  </Button>
                  <Button
                    className="admin-filter-btn"
                    variant={filter === "all" ? "primary" : "outline-primary"}
                    onClick={() => onFilterChange("all")}
                  >
                    All
                  </Button>
                </ButtonGroup>

                <Button variant="outline-secondary" size="sm" onClick={() => fetchTags(filter)}>
                  Refresh
                </Button>
              </div>
            </div>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}
          {successMsg && <Alert variant="success">{successMsg}</Alert>}

          <div className="admin-panel">
            {tags.length === 0 ? (
              <p className="mb-0">No tags for this filter 🎉</p>
            ) : (
              <Table className="admin-table" striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Tag ID</th>
                    <th>Status</th>
                    <th>Claimed At</th>
                    <th>Batch</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {tags.map((t) => {
                    const status = String(t.status || "").toLowerCase();
                    return (
                      <tr key={t.tagId}>
                        <td style={{ whiteSpace: "nowrap" }}>{t.tagId}</td>
                        <td style={{ whiteSpace: "nowrap" }}>{statusBadge(t.status)}</td>
                        <td style={{ whiteSpace: "nowrap" }}>{fmtDate(t.claimedAt)}</td>
                        <td style={{ whiteSpace: "nowrap" }}>{t.batchId || "—"}</td>

                        <td>
                          <div className="d-flex flex-column gap-1 admin-row-actions">
                            {status !== "claimed" && (
                              <Button size="sm" variant="success" onClick={() => updateStatus(t.tagId, "claimed")}>
                                Mark Claimed
                              </Button>
                            )}

                            {status !== "unclaimed" && (
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => updateStatus(t.tagId, "unclaimed")}
                              >
                                Mark Unclaimed
                              </Button>
                            )}

                            {status !== "blocked" && (
                              <Button size="sm" variant="danger" onClick={() => updateStatus(t.tagId, "blocked")}>
                                Block Tag
                              </Button>
                            )}

                            {status === "claimed" && (
                              <Button size="sm" variant="outline-primary" onClick={() => openMessageModal(t)}>
                                Text Owner
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </div>
        </div>

        {/* SMS Compose Modal */}
        <Modal className="admin-modal" show={messageModal.show} onHide={closeMessageModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>Text Owner – SmartTag {messageModal.tag?.tagId || ""}</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <p className="text-muted" style={{ fontSize: "0.9rem" }}>
              This will send an SMS to the phone number registered for this SmartTag.
              Please avoid sharing sensitive information in messages.
            </p>

            <Form.Group controlId="adminTagsMessageText">
              <Form.Label>Message</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={messageModal.text}
                onChange={(e) =>
                  setMessageModal((prev) => ({
                    ...prev,
                    text: e.target.value,
                  }))
                }
              />
            </Form.Group>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={closeMessageModal}>
              Cancel
            </Button>
            <Button variant="primary" onClick={sendMessageToTagOwner} disabled={sending}>
              {sending ? "Sending…" : "Send SMS"}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
}
